/**
 * Consulta / Mantenimiento de Solicitudes — migración web de ares2030.
 *
 * Programa de solo lectura sobre la tabla `solicit`:
 *   1. Reporte de solicitudes por rango de fechas y sucursal.
 *   2. Reimpresión del detalle de una solicitud (cliente, garante, financiamiento).
 *   3. Consulta de garante por cédula.
 *
 * El export a Excel del escritorio era código muerto y no se migró.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi, useSucursales } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";
import { etiquetaDocumento, formatDocumentoAuto } from "lib/documento";

// Extrae el código numérico de una etiqueta de sucursal "5-LOS MINA" -> "5".
// "0-TODOS" o vacío -> "" (todas).
const codigoSucursal = (label) => {
  const s = String(label || "")
    .split("-")[0]
    .trim();
  return s === "0" || s === "" ? "" : s;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin solicitudes
      </Typography>
    </Stack>
  );
}

// Reporte de solicitudes por fechas: abre ventana e imprime la tabla.
function imprimirReporte(rows, compania, rango) {
  const w = window.open("", "_blank", "width=780,height=900");
  if (!w) return;
  const filas = rows
    .map(
      (r) => `<tr>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.nosolic ?? ""}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.fecha ?? ""}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.cedula ?? ""}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.nombres ?? ""}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${
          r.cantsol ?? ""
        }</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${r.status ?? ""}</td>
      </tr>`
    )
    .join("");
  w.document.write(`
    <html><head><meta name="color-scheme" content="light"><title>Reporte de Solicitudes</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:20px;font-size:12px">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 12px;font-weight:bold">Reporte de Solicitudes ${
        rango || ""
      }</p>
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:#eee">
            <th style="padding:5px 6px;text-align:left">No. Solic.</th>
            <th style="padding:5px 6px;text-align:left">Fecha</th>
            <th style="padding:5px 6px;text-align:left">Documento</th>
            <th style="padding:5px 6px;text-align:left">Nombres</th>
            <th style="padding:5px 6px;text-align:right">Cant. Solic.</th>
            <th style="padding:5px 6px;text-align:left">Status</th>
          </tr>
        </thead>
        <tbody>${filas}</tbody>
      </table>
      <p style="margin-top:12px;font-weight:bold">Total de solicitudes: ${rows.length}</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}

// Reimpresión del detalle de una solicitud (cliente, garante, financiamiento).
function imprimirSolicitud(s, compania) {
  const w = window.open("", "_blank", "width=460,height=720");
  if (!w) return;
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const bloque = (titulo, filas) => `
    <p style="margin:14px 0 4px;font-weight:bold;border-bottom:1px solid #000">${titulo}</p>
    <table style="width:100%;border-collapse:collapse">${filas}</table>`;
  w.document.write(`
    <html><head><meta name="color-scheme" content="light"><title>Solicitud ${s.nosolic || ""}</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px">
      ${encabezadoEmpresaHtml(compania)}
      <p style="text-align:center;margin:0 0 6px;font-weight:bold">Solicitud No. ${
        s.nosolic || ""
      }</p>
      ${bloque(
        "Datos del Cliente",
        fila("Fecha", s.fecha) +
          fila(etiquetaDocumento(s.cedula), s.cedula) +
          fila("Nombres", s.nombres) +
          fila("Apodo", s.apodo) +
          fila("Dirección", s.direccion) +
          fila("Urb.", s.urb) +
          fila("Teléfono", s.telefono) +
          fila("Comentario", s.comentario)
      )}
      ${bloque(
        "Datos del Garante",
        fila("Fiador", s.fiador) +
          fila(`${etiquetaDocumento(s.fiadorced)} Fiador`, s.fiadorced) +
          fila("Dirección", s.fiadordir) +
          fila("Urb.", s.urbfiad) +
          fila("Teléfono", s.fiadortel) +
          fila("Edad", s.edadfiad)
      )}
      ${bloque(
        "Datos del Financiamiento",
        fila("No. Préstamo", s.noprest) +
          fila("Cant. Solicitada", s.cantsol) +
          fila("Plazo", s.plazo) +
          fila("Cuotas", s.cuotas)
      )}
      <p style="margin-top:30px;border-top:1px solid #000;padding-top:6px;text-align:center">Revisado Por</p>
      <p style="text-align:right;font-size:11px;color:#666">User: ${s.user || ""}</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
}

export default function ConsultaSolicitudes() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ conTodos: true });

  const [compania, setCompania] = useState("");
  useEffect(() => {
    let activo = true;
    post("/api/ares2030_config", {}).then(({ ok, data }) => {
      if (!activo || !ok) return;
      setCompania(data.compania || "");
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Reporte por fechas ----
  const [sucursal, setSucursal] = useState("0-TODOS");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/ares2030_reporte", {
        sucursal: codigoSucursal(sucursal),
        fechadesde: fecha1,
        fechahasta: fecha2,
      });
      if (ok) {
        setRows((data.solicitudes || []).map((r, i) => ({ id: i, ...r })));
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  }, [post, sucursal, fecha1, fecha2, showAlert]);

  const limpiar = () => {
    setSucursal("0-TODOS");
    setFecha1(inicioMesISO());
    setFecha2(hoyISO());
    setRows([]);
  };

  const columns = [
    { field: "nosolic", headerName: "No. Solic.", width: 120 },
    { field: "fecha", headerName: "Fecha", width: 120 },
    { field: "cedula", headerName: "Documento", width: 150, valueFormatter: (value) => formatDocumentoAuto(value) },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 200 },
    {
      field: "cantsol",
      headerName: "Cant. Solic.",
      width: 130,
      align: "right",
      headerAlign: "right",
    },
    { field: "status", headerName: "Status", width: 110 },
  ];

  // ---- Reimpresión de solicitud ----
  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [nosolicSel, setNosolicSel] = useState(null);
  const [reimprimiendo, setReimprimiendo] = useState(false);
  const buscarTimer = useRef(null);
  const buscarSeq = useRef(0);

  // Búsqueda server-side con debounce; se ignoran respuestas fuera de orden
  // para que una consulta lenta no pise los resultados de la más reciente.
  const buscarSolicitudes = useCallback(
    (q) => {
      clearTimeout(buscarTimer.current);
      buscarTimer.current = setTimeout(async () => {
        const seq = ++buscarSeq.current;
        setBuscando(true);
        try {
          const { ok, data } = await post("/api/ares2030_solicitudes", {
            q,
            sucursal: codigoSucursal(sucursal),
          });
          if (seq === buscarSeq.current && ok && Array.isArray(data.solicitudes))
            setOpts(data.solicitudes);
        } finally {
          if (seq === buscarSeq.current) setBuscando(false);
        }
      }, 300);
    },
    [post, sucursal]
  );

  useEffect(() => () => clearTimeout(buscarTimer.current), []);

  const reimprimir = async () => {
    const nosolic = typeof nosolicSel === "string" ? nosolicSel : nosolicSel?.nosolic;
    if (!nosolic) {
      showAlert({ message: "Seleccione o escriba un número de solicitud", color: "warning" });
      return;
    }
    setReimprimiendo(true);
    try {
      const { ok, data } = await post("/api/ares2030_reimprimir", {
        nosolic: String(nosolic),
        sucursal: codigoSucursal(sucursal),
      });
      if (ok && data.solicitud) {
        imprimirSolicitud(data.solicitud, compania);
      } else {
        showAlert({ message: data.Error || "No se encontró la solicitud", color: "error" });
      }
    } finally {
      setReimprimiendo(false);
    }
  };

  // ---- Consulta de garante ----
  const [cedulaGar, setCedulaGar] = useState("");
  const [garante, setGarante] = useState(null);
  const [consultandoGar, setConsultandoGar] = useState(false);

  const consultarGarante = async () => {
    if (!cedulaGar.trim()) {
      showAlert({ message: "Indique la cédula del garante", color: "warning" });
      return;
    }
    setConsultandoGar(true);
    setGarante(null);
    try {
      const { ok, data } = await post("/api/ares2030_garante", { cedula: cedulaGar.trim() });
      if (ok) {
        setGarante(data);
      } else {
        showAlert({ message: data.Error || "No hay datos para esta cédula", color: "error" });
      }
    } finally {
      setConsultandoGar(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Consulta de Solicitudes"
        subtitle="Reporte, reimpresión y consulta de garante sobre las solicitudes"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Reporte por fechas */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.navy}>
            Reporte de Solicitudes
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={s.label}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1.2}>
                <Button
                  startIcon={
                    loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                  }
                  onClick={cargar}
                  disabled={loading}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Consultar
                </Button>
                <Button
                  startIcon={<RestartAltIcon />}
                  onClick={limpiar}
                  sx={btnOutline(FP.color.navy)}
                >
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, mb: 1 }}>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
              Total: {rows.length}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={() => imprimirReporte(rows, compania, `del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`)}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "200px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>

        {/* Reimpresión de solicitud */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PrintOutlinedIcon} color={FP.color.accentBlue}>
            Reimprimir Solicitud
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <Autocomplete
                freeSolo
                autoHighlight
                // La búsqueda es server-side: sin esto, el Autocomplete vuelve a
                // filtrar las opciones contra el label y descarta resultados
                // (p. ej. al buscar por cédula, que no aparece en el label).
                filterOptions={(x) => x}
                options={opts}
                loading={buscando}
                value={nosolicSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.nosolic} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.nosolic || o) === (v.nosolic || v)}
                onInputChange={(_, val, reason) => {
                  if (reason === "input" && val && val.length >= 1) buscarSolicitudes(val);
                }}
                onChange={(_, v) => setNosolicSel(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.nosolic}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.nosolic}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="No. de solicitud o cliente" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                startIcon={
                  reimprimiendo ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PrintOutlinedIcon />
                  )
                }
                onClick={reimprimir}
                disabled={reimprimiendo}
                sx={btnSolid(FP.color.accentBlue, FP.color.accentBlue)}
              >
                Reimprimir
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Consulta de garante */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PersonSearchOutlinedIcon} color={FP.color.info}>
            Consulta de Garante
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={4}>
              <TextField
                fullWidth
                label="Cédula del garante"
                value={cedulaGar}
                onChange={(e) => setCedulaGar(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  consultandoGar ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={consultarGarante}
                disabled={consultandoGar}
                sx={btnSolid(FP.color.info, FP.color.info)}
              >
                Consultar
              </Button>
            </Grid>
          </Grid>
          {garante && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  fullWidth
                  label="Fiador"
                  value={garante.fiador || ""}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <TextField
                  fullWidth
                  label="Dirección"
                  value={garante.fiadordir || ""}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={4}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={garante.fiadortel || ""}
                  InputProps={{ readOnly: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}
